import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useAppSelector, useAppDispatch } from './store/hooks';
import { fetchCurrentUser } from './store/slices/authSlice';
import { loadAccountSettings } from './store/loadAccountSettings';
import { useTranslation } from './i18n';
import ErrorBoundary from './components/ErrorBoundary';
import AuthGuard from './components/AuthGuard';
import AgeGate from './components/AgeGate';
import LockScreen from './components/LockScreen';
import PrivacyShield from './components/PrivacyShield';
import { ToastProvider } from './components/ui';
import RealtimeBridge from './realtime/RealtimeBridge';
import { useVerifiedParam } from './hooks/useVerifiedParam';
import PageMeta from './seo/PageMeta';
import GuestSheets from './components/guest/GuestSheets';

// Landing and login are bundled with the app shell: they are what a signed-out
// visitor sees first. Every other page is its own chunk.
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import { hasSeenOnboarding } from './gameplay/onboardingStorage';
import { readPairCode } from './gameplay/pairCodeStorage';
import { confirmAge, hasConfirmedAge } from './security/ageGateStorage';
import { useAppLock } from './security/useAppLock';
import { useDiscreet, useTabDisguise } from './security/discreet';
import { DECOY_PATH } from './security/panicExit';

const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const GuestStartPage = lazy(() => import('./pages/GuestStartPage'));
const ForgetPasswordPage = lazy(() => import('./pages/ForgetPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const GamePage = lazy(() => import('./pages/GamePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const HistoryPage = lazy(() => import('./pages/HistoryPage'));
const FantasyCatalogPage = lazy(() => import('./pages/FantasyCatalogPage'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
const PairPage = lazy(() => import('./pages/PairPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DecoyPage = lazy(() => import('./pages/DecoyPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const LegalIndexPage = lazy(() => import('./pages/legal/LegalIndexPage'));
const LegalTermsPage = lazy(() =>
  import('./pages/legal/LegalPages').then(m => ({ default: m.LegalTermsPage }))
);
const LegalPrivacyPage = lazy(() =>
  import('./pages/legal/LegalPages').then(m => ({
    default: m.LegalPrivacyPage,
  }))
);
const LegalCookiesPage = lazy(() =>
  import('./pages/legal/LegalPages').then(m => ({
    default: m.LegalCookiesPage,
  }))
);
const LegalAgePage = lazy(() =>
  import('./pages/legal/LegalPages').then(m => ({ default: m.LegalAgePage }))
);

// Help centre: lazy so its content and illustrations stay out of the main chunk.
const HelpHubPage = lazy(() => import('./pages/help/HelpHubPage'));
const PlaybookPage = lazy(() => import('./pages/help/PlaybookPage'));
const ArticlePage = lazy(() => import('./pages/help/ArticlePage'));
const CatalogPage = lazy(() => import('./pages/help/CatalogPage'));
const CatalogEntryPage = lazy(() => import('./pages/help/CatalogEntryPage'));
const FaqPage = lazy(() => import('./pages/help/FaqPage'));
const SupportPage = lazy(() => import('./pages/help/SupportPage'));
const HelpNotFoundPage = lazy(() => import('./pages/help/HelpNotFoundPage'));

function HelpRouteFallback() {
  const { t } = useTranslation();
  return (
    <div className="min-h-dvh flex items-center justify-center">
      <p className="text-white/60" role="status">
        {t('common.loading')}
      </p>
    </div>
  );
}

/** Help pages are public: people need them to sign in. */
function helpRoute(page: ReactNode) {
  return <Suspense fallback={<HelpRouteFallback />}>{page}</Suspense>;
}

/** Redirects first-time users to the intro before showing the dashboard. */
function OnboardingGate({ children }: { children: ReactNode }) {
  if (!hasSeenOnboarding()) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/** Where a freshly signed-in visitor lands: back on `/pair` if they came for an invite. */
function postAuthPath(): string {
  return readPairCode() ? '/pair' : '/dashboard';
}

const PUBLIC_AUTH_PATHS = new Set([
  '/start',
  '/login',
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/reset-password',
]);

/**
 * Help and legal pages stay reachable before the age gate: people need help to
 * sign in, and the gate itself links to the legal texts.
 */
function isOpenBeforeAgeGate(pathname: string): boolean {
  return (
    pathname === '/help' ||
    pathname.startsWith('/help/') ||
    pathname === '/legal' ||
    pathname.startsWith('/legal/')
  );
}

/** Shown while a page chunk downloads: the brand mark, nothing heavy. */
function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex min-h-dvh flex-col items-center justify-center gap-4"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-hc-accent/15 text-hc-accent-soft motion-safe:animate-pulse">
        <Heart className="h-7 w-7" aria-hidden="true" />
      </span>
      <p className="text-sm text-white/60">{t('app.route.loading')}</p>
    </div>
  );
}

/** Mounted inside the toast provider so the hook can toast. */
function VerifiedParamHandler() {
  useVerifiedParam();
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, offlineStale } = useAppSelector(
    state => state.auth
  );
  const isPublicAuthRoute = PUBLIC_AUTH_PATHS.has(location.pathname);
  const { locked, unlock } = useAppLock();
  const [ageConfirmed, setAgeConfirmed] = useState<boolean>(hasConfirmedAge);
  const isDecoyRoute = location.pathname === DECOY_PATH;

  // Hydrate auth state from session cookie on mount
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, isAuthenticated]);

  // Offline reload: the shell shows the last known user (`offlineStale`). The
  // session is checked again, and settings load, once the browser is online.
  useEffect(() => {
    if (!offlineStale) return undefined;
    const refetch = () => {
      void dispatch(fetchCurrentUser());
    };
    window.addEventListener('online', refetch);
    return () => window.removeEventListener('online', refetch);
  }, [dispatch, offlineStale]);

  useEffect(() => {
    if (isAuthenticated && !offlineStale) {
      dispatch(loadAccountSettings());
    }
  }, [dispatch, isAuthenticated, offlineStale]);

  // P1-S1: while locked the app tree is not rendered at all. The decoy notes
  // page is the one exception: it holds nothing, and it is where a panic exit
  // lands; leaving it for the app meets the lock.
  if (locked && !isDecoyRoute) {
    return <LockScreen onUnlock={unlock} />;
  }

  // P1-S4: once per device, before any page except help and the decoy.
  if (
    !ageConfirmed &&
    !isDecoyRoute &&
    !isOpenBeforeAgeGate(location.pathname)
  ) {
    return (
      <AgeGate
        onConfirm={() => {
          confirmAge();
          setAgeConfirmed(true);
        }}
      />
    );
  }

  // Show loading state while hydrating from cookie (not on auth pages)
  if (isLoading && !isAuthenticated && !isPublicAuthRoute && !isDecoyRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-hc-accent rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 border-4 border-hc-cream border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="text-white/60">{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ToastProvider>
        <RealtimeBridge />
        <VerifiedParamHandler />
        <GuestSheets />
        <div className="App">
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <LandingPage />
                  )
                }
              />
              <Route
                path="/login"
                element={<Navigate to="/auth/login" replace />}
              />
              <Route
                path="/auth/login"
                element={
                  isAuthenticated ? (
                    <Navigate to={postAuthPath()} replace />
                  ) : (
                    <LoginPage />
                  )
                }
              />
              <Route
                path="/auth/register"
                element={
                  isAuthenticated ? (
                    <Navigate to={postAuthPath()} replace />
                  ) : (
                    <RegisterPage />
                  )
                }
              />
              {/* Play without an account: the page redirects a signed-in visitor itself */}
              <Route path="/start" element={<GuestStartPage />} />
              <Route
                path="/auth/forgot-password"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <ForgetPasswordPage />
                  )
                }
              />
              <Route
                path="/reset-password"
                element={
                  isAuthenticated ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <ResetPasswordPage />
                  )
                }
              />

              {/* Help centre (public; uses the app shell when signed in) */}
              <Route path="/help" element={helpRoute(<HelpHubPage />)} />
              <Route
                path="/help/playbook"
                element={helpRoute(<PlaybookPage />)}
              />
              <Route
                path="/help/playbook/:id"
                element={helpRoute(<ArticlePage />)}
              />
              <Route
                path="/help/catalog"
                element={helpRoute(<CatalogPage />)}
              />
              <Route
                path="/help/catalog/:id"
                element={helpRoute(<CatalogEntryPage />)}
              />
              <Route path="/help/faq" element={helpRoute(<FaqPage />)} />
              <Route
                path="/help/support"
                element={helpRoute(<SupportPage />)}
              />
              <Route path="/help/*" element={helpRoute(<HelpNotFoundPage />)} />

              {/* Decoy notes page: no auth, no shell, stores nothing (P1-S3) */}
              <Route path={DECOY_PATH} element={<DecoyPage />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <AuthGuard>
                    <OnboardingGate>
                      <DashboardPage />
                    </OnboardingGate>
                  </AuthGuard>
                }
              />

              <Route
                path="/onboarding"
                element={
                  <AuthGuard>
                    <OnboardingPage />
                  </AuthGuard>
                }
              />

              {/* Invite links land here signed out too: the page keeps the code through sign-in */}
              <Route path="/pair" element={<PairPage />} />

              <Route
                path="/partner"
                element={
                  <AuthGuard>
                    <PartnerPage />
                  </AuthGuard>
                }
              />

              <Route
                path="/rewards"
                element={
                  <AuthGuard>
                    <RewardsPage />
                  </AuthGuard>
                }
              />

              <Route
                path="/game"
                element={
                  <AuthGuard>
                    <GamePage />
                  </AuthGuard>
                }
              />

              <Route
                path="/game/:id"
                element={
                  <AuthGuard>
                    <GamePage />
                  </AuthGuard>
                }
              />

              <Route
                path="/settings"
                element={
                  <AuthGuard>
                    <SettingsPage />
                  </AuthGuard>
                }
              />

              <Route
                path="/history"
                element={
                  <AuthGuard>
                    <HistoryPage />
                  </AuthGuard>
                }
              />

              <Route
                path="/fantasy-catalog"
                element={
                  <AuthGuard>
                    <FantasyCatalogPage />
                  </AuthGuard>
                }
              />

              {/* Everything is free: the old subscription page is gone */}
              <Route
                path="/subscription"
                element={<Navigate to="/dashboard" replace />}
              />

              {/* Legal texts: public, reachable before the age gate */}
              <Route path="/legal" element={<LegalIndexPage />} />
              <Route path="/legal/terms" element={<LegalTermsPage />} />
              <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
              <Route path="/legal/cookies" element={<LegalCookiesPage />} />
              <Route path="/legal/age" element={<LegalAgePage />} />

              {/* Anything else */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </div>
      </ToastProvider>
    </ErrorBoundary>
  );
}

function App() {
  const discreet = useDiscreet();
  useTabDisguise(discreet);

  return (
    <>
      <PageMeta />
      <PrivacyShield />
      <AppRoutes />
    </>
  );
}

export default App;
